import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Facebook, Linkedin, Twitter, CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site";
import PageHero, { ScheduleConsultationButton } from "@/components/site/PageHero";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";

const SERVICE_OPTIONS = [
  "Individual Tax Services",
  "Business Financial Services",
  "Nonprofit & Ministry Services",
  "Business Foundation Services",
  "Executive Financial Command™",
  "Tax School",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organization: "", service_interest: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function update(field, value) { setForm((f) => ({ ...f, [field]: value })); }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    try {
      setLoading(true);
      await submitContact(form);
      toast.success("Message sent.", { description: "Tina and the TRJ team will be in touch shortly." });
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", organization: "", service_interest: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Submission failed.", { description: detail ? String(detail) : "Please try again or call us directly." });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact TRJ Services"
        title="Let's talk about your next financial chapter."
        subtitle="Whether you need tax help, business support, nonprofit guidance, or executive financial leadership — we're ready when you are."
        image={SITE.assets.officeModern}
      >
        <ScheduleConsultationButton testid="contact-hero-schedule-btn" />
        <a href={SITE.phoneHref} className="trj-btn-outline-white" data-testid="contact-hero-call-btn">
          <Phone className="w-4 h-4" /> {SITE.phone}
        </a>
      </PageHero>

      <section className="bg-white py-20 md:py-24" data-testid="contact-main">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
          {/* INFO COLUMN */}
          <aside className="lg:col-span-5 space-y-8">
            <div>
              <div className="trj-eyebrow mb-3">Get in Touch</div>
              <h2 className="font-serif-trj text-3xl md:text-4xl text-[var(--trj-navy)] leading-tight">We respond quickly.</h2>
              <p className="mt-4 text-[var(--trj-muted)] leading-relaxed">
                For the fastest path forward, book a consultation. For general questions, send us a note
                — we typically reply within one business day.
              </p>
            </div>

            <div className="space-y-5 text-[15px] text-[var(--trj-navy)]">
              <InfoRow icon={Phone} label="Phone" value={SITE.phone} href={SITE.phoneHref} testid="contact-info-phone" />
              <InfoRow icon={Mail} label="Email" value={SITE.email} href={SITE.emailHref} testid="contact-info-email" />
              <InfoRow icon={MapPin} label="Office" value={SITE.address} testid="contact-info-address" />
              <InfoRow icon={Clock} label="Hours" value={SITE.hours} testid="contact-info-hours" />
            </div>

            <div>
              <div className="trj-eyebrow mb-3">Connect</div>
              <div className="flex gap-3">
                <a href={SITE.socials.facebook} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-300 flex items-center justify-center text-[var(--trj-navy)] hover:bg-[var(--trj-navy)] hover:text-white transition-colors" data-testid="contact-social-facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href={SITE.socials.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-300 flex items-center justify-center text-[var(--trj-navy)] hover:bg-[var(--trj-navy)] hover:text-white transition-colors" data-testid="contact-social-linkedin">
                  <Linkedin className="w-4 h-4" />
                </a>
                <a href={SITE.socials.twitter} target="_blank" rel="noreferrer" className="w-10 h-10 border border-gray-300 flex items-center justify-center text-[var(--trj-navy)] hover:bg-[var(--trj-navy)] hover:text-white transition-colors" data-testid="contact-social-twitter">
                  <Twitter className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-[var(--trj-light)] p-6 border-l-4 border-[var(--trj-gold)]">
              <div className="font-serif-trj text-xl text-[var(--trj-navy)] leading-snug">
                &ldquo;Stronger financial systems create stronger organizations, stronger families, and stronger communities.&rdquo;
              </div>
              <div className="mt-2 text-xs uppercase tracking-widest text-[var(--trj-green)]">— Tina Jacobs</div>
            </div>
          </aside>

          {/* FORM */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white border border-gray-200 p-8 md:p-10 shadow-sm"
            >
              <div className="trj-eyebrow mb-3">Send a Message</div>
              <h3 className="font-serif-trj text-2xl md:text-3xl text-[var(--trj-navy)] leading-tight">
                Tell us about your situation.
              </h3>
              <div className="trj-divider-gold mt-5" />

              <div className="mt-7 grid sm:grid-cols-2 gap-5">
                <Field label="Full Name *" required>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    className="rounded-none"
                    data-testid="contact-form-name"
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Email *" required>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    className="rounded-none"
                    data-testid="contact-form-email"
                    placeholder="you@example.com"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    className="rounded-none"
                    data-testid="contact-form-phone"
                    placeholder="708-555-0100"
                  />
                </Field>
                <Field label="Organization">
                  <Input
                    value={form.organization}
                    onChange={(e) => update("organization", e.target.value)}
                    className="rounded-none"
                    data-testid="contact-form-organization"
                    placeholder="Business / Nonprofit name (optional)"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Service Interest">
                    <Select value={form.service_interest} onValueChange={(v) => update("service_interest", v)}>
                      <SelectTrigger className="rounded-none" data-testid="contact-form-service">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message *" required>
                    <Textarea
                      required
                      rows={6}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      className="rounded-none"
                      data-testid="contact-form-message"
                      placeholder="Briefly describe your situation, goals, or questions…"
                    />
                  </Field>
                </div>
              </div>

              <div className="mt-7 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-[var(--trj-muted)] max-w-md">
                  By submitting, you agree to be contacted by TRJ Services regarding your inquiry.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  className="trj-btn-green text-sm disabled:opacity-60"
                  data-testid="contact-form-submit"
                >
                  <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send Message"}
                </button>
              </div>

              {submitted && (
                <div className="mt-6 p-4 bg-[var(--trj-green)]/10 border border-[var(--trj-green)]/30 flex items-start gap-3 text-sm text-[var(--trj-navy)]" data-testid="contact-success-banner">
                  <CheckCircle2 className="w-5 h-5 text-[var(--trj-green)] mt-0.5" />
                  <div>
                    <div className="font-semibold">Thanks — your message is on the way.</div>
                    <div className="text-[var(--trj-muted)] mt-1">We&apos;ll be in touch within one business day. For urgent matters, please call {SITE.phone}.</div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <>
      <div className="w-10 h-10 bg-[var(--trj-light)] text-[var(--trj-green)] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--trj-muted)]">{label}</div>
        <div className="text-[15px] font-medium text-[var(--trj-navy)] mt-0.5">{value}</div>
      </div>
    </>
  );
  return href ? (
    <a href={href} className="flex items-start gap-3 hover:text-[var(--trj-green)]" data-testid={testid}>{inner}</a>
  ) : (
    <div className="flex items-start gap-3" data-testid={testid}>{inner}</div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-[var(--trj-muted)]">
        {label}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
